import { extractNode, extractNodeList, toIntCount } from '../graph-row'
import type {
  CypherReadResult,
  PlantEntityConnectionGroup,
  PlantEntityConnectionRow,
  PlantEntityResult,
  PlantGenericEntity,
  PlantLabelCount,
  PlantLabelEntitiesPage
} from '../types'
import { extractPlantEdges } from './edges'

export function mapPlantEntity(raw: CypherReadResult): PlantEntityResult | null {
  const row = raw.results[0]
  if (!row) return null
  const entity = extractNode<PlantGenericEntity>(row['n'])
  if (!entity) return null
  const labels = Array.isArray(row['labels'])
    ? row['labels'].filter((label): label is string => typeof label === 'string')
    : []
  const strayLabels = Array.isArray(row['strayLabels'])
    ? row['strayLabels'].filter((label): label is string => typeof label === 'string')
    : []
  return { labels, entity, strayLabels }
}

export function mapPlantEntityConnections(
  raw: CypherReadResult,
  entityId: string
): PlantEntityConnectionGroup[] {
  const groups: PlantEntityConnectionGroup[] = []
  for (const group of raw.results) {
    const type = typeof group['type'] === 'string' ? group['type'] : ''
    const direction = group['direction'] === 'out' ? 'out' : 'in'
    if (!type) continue

    const rows: PlantEntityConnectionRow[] = []
    const rawRows = Array.isArray(group['rows']) ? group['rows'] : []
    for (const value of rawRows) {
      if (!value || typeof value !== 'object') continue
      const item = value as Record<string, unknown>
      const peer = extractNode<PlantGenericEntity>(item['peer'])
      if (!peer) continue
      const peerLabels = Array.isArray(item['peerLabels'])
        ? item['peerLabels'].filter((label): label is string => typeof label === 'string')
        : []
      const edge = extractPlantEdges([
        {
          type,
          source: direction === 'out' ? entityId : peer.id,
          target: direction === 'out' ? peer.id : entityId,
          props: item['props']
        }
      ])[0]
      if (edge) rows.push({ peer, peerLabels, edge })
    }

    groups.push({
      type,
      direction,
      total: toIntCount(group['total']),
      rows
    })
  }
  return groups
}

export function mapPlantLabelCounts(raw: CypherReadResult): PlantLabelCount[] {
  return raw.results
    .map((row) => {
      const label = typeof row['label'] === 'string' ? row['label'] : null
      return label ? { label, total: toIntCount(row['total']) } : null
    })
    .filter((row): row is PlantLabelCount => row !== null)
}

export function mapPlantLabelEntitiesPage(
  raw: CypherReadResult,
  label: PlantLabelEntitiesPage['label']
): PlantLabelEntitiesPage {
  const row = raw.results[0] ?? {}
  const strayIdsRaw = row['strayIds']
  const strayIds = Array.isArray(strayIdsRaw)
    ? strayIdsRaw.filter((id): id is string => typeof id === 'string')
    : []
  return {
    label,
    total: toIntCount(row['total']),
    entities: extractNodeList<PlantGenericEntity>(row['entities']),
    strayIds
  }
}
